import React, { Component } from "react";
import NumberFormat from "react-number-format";
import { postRequest } from "../../../utils/httpRequest";
import { Hero } from "./Types";

interface Props {
  hero: Hero;
  idx: number;
  slug: string;
  page: number;
  count: number;
  handleSelectMember: (
    e: any,
    value: boolean,
    idx: number,
    key: string,
  ) => void;
}

interface State {
  visible: boolean;
}

const initials = (name: string) =>
  name
    .replace(/[^\w\s]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

// Real table rows rather than the nested divs this used to be, so screen
// readers get the column headers with each cell and the detail panel can be
// properly associated with its trigger.
class TeamMember extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = { visible: false };
  }

  toggleVisible = () => this.setState({ visible: !this.state.visible });

  // Both toggles below post optimistically and roll the row back if the
  // request fails -- unchanged behaviour, just moved into the new markup.
  setAdmin = (event) => {
    const { idx, hero, handleSelectMember, slug } = this.props;
    const previousValue = hero.admin;

    handleSelectMember(event, !hero.admin, idx, "admin");

    postRequest(`/api/teams/${slug}/manage_members.json`, {
      situation: "manage_admin",
      user_ids: [hero.id],
    }).catch(() => handleSelectMember(event, previousValue, idx, "admin"));
  };

  setShowOnBoard = (event) => {
    const { idx, hero, handleSelectMember, slug } = this.props;
    const previousValue = hero.show_on_board;

    handleSelectMember(event, !hero.show_on_board, idx, "show_on_board");

    postRequest(`/api/teams/${slug}/manage_members.json`, {
      situation: "manage_leaderboard",
      user_ids: [hero.id],
    }).catch(() =>
      handleSelectMember(event, previousValue, idx, "show_on_board"),
    );
  };

  render() {
    const { hero, handleSelectMember, idx, page, count } = this.props;
    const { visible } = this.state;

    const rank = idx + 1 + page * count;
    const detailId = `member-detail-${hero.id}`;
    const checkboxId = `member-select-${hero.id}`;

    return (
      <>
        <tr className={`lb-row ${hero.selected ? "is-selected" : ""}`}>
          <td className="col-check" data-label="Select">
            <label className="sr-only" htmlFor={checkboxId}>
              Select {hero.protected_name}
            </label>
            <input
              type="checkbox"
              className="cb"
              id={checkboxId}
              checked={hero.selected}
              onChange={(event) =>
                handleSelectMember(event, event.target.checked, idx, "selected")
              }
            />
          </td>

          <td className="col-rank" data-label="Rank">
            <span className={`rank-badge${rank <= 3 ? ` r${rank}` : ""}`}>
              {rank}
            </span>
          </td>

          <td className="col-name" data-label="Name">
            <div className="name-cell">
              <span className="av">
                {hero.profile_picture_url ? (
                  <img src={hero.profile_picture_url} alt="" />
                ) : (
                  initials(hero.protected_name)
                )}
              </span>
              <span className="name-tx">
                <span className="n">{hero.protected_name}</span>
                {hero.admin && <span className="tag-admin">Admin</span>}
              </span>
            </div>
          </td>

          <td className="col-courses" data-label="Courses">
            {hero.enrolments?.length || 0}
          </td>

          <td className="col-points" data-label="Points">
            <span className="points">
              <NumberFormat
                value={hero.xp}
                displayType="text"
                thousandSeparator
              />
            </span>
          </td>

          <td className="col-activity" data-label="Last activity">
            <span className="activity">{hero.last_activity}</span>
          </td>

          <td className="col-expand">
            <button
              className={`expand-btn ${visible ? "is-open" : ""}`}
              type="button"
              aria-expanded={visible}
              aria-controls={detailId}
              aria-label={`Course detail for ${hero.protected_name}`}
              onClick={this.toggleVisible}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          </td>
        </tr>

        <tr className="detail-row" id={detailId} hidden={!visible}>
          <td colSpan={7}>
            <div className="detail">
              <div className="detail-progress">
                <h3>Course progress</h3>
                {hero.enrolments?.length > 0 ? (
                  <div className="course-prog">
                    {hero.enrolments.map((enrolment, index) => (
                      <div className="cp-row" key={index}>
                        <span className="cp-name">{enrolment.course_name}</span>
                        <span className="cp-bar">
                          <span
                            className={`cp-fill ${
                              enrolment.percentage_completed >= 100 ? "done" : ""
                            }`}
                            style={{
                              width: `${enrolment.percentage_completed}%`,
                            }}
                          />
                        </span>
                        <span className="cp-pct">
                          {enrolment.percentage_completed}%
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="detail-empty">
                    No courses started yet.
                  </p>
                )}
              </div>

              <div className="detail-actions">
                <h3>Permissions</h3>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={hero.admin}
                    onChange={this.setAdmin}
                  />
                  <span className="switch-track" aria-hidden="true" />
                  <span className="switch-label">Team admin</span>
                </label>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={hero.show_on_board}
                    onChange={this.setShowOnBoard}
                  />
                  <span className="switch-track" aria-hidden="true" />
                  <span className="switch-label">Show on leaderboard</span>
                </label>
              </div>
            </div>
          </td>
        </tr>
      </>
    );
  }
}

export default TeamMember;
